import { Button, Typography } from '@/components/ui';
import { cn } from '@/lib/utils';
import { forwardRef, type HTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';

export interface GlobalErrorPageProps extends HTMLAttributes<HTMLDivElement> {
  error?: Error;
  onRetry?: () => void;
  onReset?: () => void;
  className?: string;
}

export const GlobalErrorPage = forwardRef<HTMLDivElement, GlobalErrorPageProps>(
  ({ error, onRetry, onReset, className, ...props }, ref) => {
    const navigate = useNavigate();
    const isDev = import.meta.env.DEV;

    const handleRefresh = () => {
      window.location.reload();
    };

    const handleGoHome = () => {
      navigate('/');
    };

    return (
      <div
        ref={ref}
        className={cn(
          'min-h-screen bg-primary-600 relative overflow-hidden',
          className
        )}
        {...props}
      >
        {/* Background Ellipses - same pattern as other pages */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top right purple ellipse */}
          <div className="absolute w-[503px] h-[503px] -right-[287px] -top-[197px] rounded-full bg-secondary-600/[0.13] blur-[85.8px] z-[1]" />

          {/* Bottom left yellow ellipse */}
          <div className="absolute w-[223px] h-[223px] -left-[82px] -bottom-[100px] rounded-full bg-primary-600/[0.14] blur-[85.8px] z-[2]" />

          {/* Bottom right ellipse */}
          <div className="absolute w-[223px] h-[223px] -right-[147px] bottom-[20px] rounded-full bg-primary-400/20 blur-[60px] z-[1]" />
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Header with Logo */}
          <div className="px-6 py-6 md:px-12">
            <div className="flex justify-center">Logo Here</div>
          </div>

          {/* Main Error Content */}
          <div className="flex-1 flex items-center justify-center px-6 md:px-12">
            <div className="max-w-md w-full text-center">
              {/* Error Icon */}
              <div className="mb-8">
                <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-10 h-10 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>

                {/* Title */}
                <Typography variant="heading-white-center" className="mb-4">
                  Oops! Something went wrong
                </Typography>

                {/* Description */}
                <Typography variant="body-text" className="mb-8 opacity-90">
                  We're experiencing a technical issue. This usually resolves
                  quickly, but you can try refreshing the page or come back in a
                  few minutes.
                </Typography>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <Button
                  onClick={onRetry || handleRefresh}
                  variant="secondary"
                  size="lg"
                  className="w-full"
                >
                  Try Again
                </Button>

                <Button
                  onClick={onReset || handleGoHome}
                  variant="outline"
                  size="lg"
                  className="w-full"
                >
                  Go to Home
                </Button>
              </div>

              {/* Support Link */}
              <div className="mt-8">
                <Typography
                  variant="body-text"
                  className="text-sm opacity-75 mb-2"
                >
                  Still having trouble?
                </Typography>
                <button
                  onClick={() =>
                    window.open('mailto:support@mypaynow.com.au', '_blank')
                  }
                  className="text-white underline text-sm hover:no-underline transition-all"
                >
                  Contact Support
                </button>
              </div>

              {/* Development Error Details */}
              {isDev && error && (
                <details className="mt-8 text-left">
                  <summary className="text-white/70 text-sm cursor-pointer hover:text-white transition-colors">
                    🔧 Developer Details
                  </summary>
                  <div className="mt-4 p-4 bg-white/10 rounded-lg">
                    <div className="text-white/90 text-sm font-mono">
                      <div className="mb-2">
                        <strong>Error:</strong> {error.message}
                      </div>
                      {error.stack && (
                        <div className="text-xs text-white/70">
                          <strong>Stack:</strong>
                          <pre className="mt-1 overflow-auto max-h-32 whitespace-pre-wrap">
                            {error.stack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </details>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-6 md:px-12">
            <div className="text-center">
              <Typography variant="caption-gray" className="text-white/60">
                MyPayNow Support - Making payments simple and secure
              </Typography>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

GlobalErrorPage.displayName = 'GlobalErrorPage';

export default GlobalErrorPage;
